import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch, useSelector } from "react-redux";
import {
  getDefaultSurveyRooomItemAction,
  setUpdateDefaultSurveyRoomItemResponse,
  updateDefaultSurveyRoomItemAction,
} from "../../../../store/reducers/settingSlice";

interface itemProps {
  surveyRoomList: any;
  setDirty: (dirty: boolean) => void;
  isDirty: boolean;
}

const SurveyItemList: React.FC<itemProps> = ({
  surveyRoomList,
  setDirty,
  isDirty,
}) => {
  const dispatch = useDispatch();

  const { defaultSurveyRoomItems, updateSurveyRoomItemResponse } = useSelector(
    (state: any) => state?.setting,
  );

  const [selectedRoomIndex, setSelectedRoomIndex] = useState(0);

  const [editItemId, setEditItemId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const [newItem, setNewItem] = useState("");
  const [showNewItemField, setShowNewItemField] = useState(false);

  const selectedRoom = surveyRoomList?.rooms?.[selectedRoomIndex];
  const isInProgress = surveyRoomList?.isSurveyInProgress;

  // Fetch items when room changes
  useEffect(() => {
    if (selectedRoom?.id) {
      dispatch(getDefaultSurveyRooomItemAction({ id: selectedRoom.id }));
    }
    dispatch(setUpdateDefaultSurveyRoomItemResponse(false));
  }, [selectedRoom?.id, updateSurveyRoomItemResponse]);

  // Edit click
  const handleEdit = (item: any) => {
    setEditItemId(item.id);
    setEditValue(item.name);
    setDirty(true);
  };

  // Save edited item
  const handleSaveEdit = () => {
    dispatch(
      updateDefaultSurveyRoomItemAction({
        id: editItemId,
        name: editValue,
      }),
    );
    setEditItemId(null);
    setDirty(false);
  };

  // Add new item click
  const handleAddItem = () => {
    setShowNewItemField(true);
    setDirty(true);
  };

  // Save new item
  const handleSaveNewItem = () => {
    dispatch(
      updateDefaultSurveyRoomItemAction({
        room_id: selectedRoom.id,
        name: newItem,
      }),
    );
    setNewItem("");
    setShowNewItemField(false);
    setDirty(false);
  };

  return (
    <Grid container spacing={0.5}>
      {/* LEFT ROOMS */}
      <Grid item xs={4}>
        <Box
          sx={{
            border: "1px solid #d1cfcf",
            borderRadius: "6px",
            height: "70vh",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              background: "#d7e3e2",
              padding: "6px",
              textAlign: "center",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            Areas
          </Box>

          <Box
            sx={{
              padding: "8px",
              height: "calc(100% - 32px)",
              overflowY: "auto",
              background: "#e9f1ee",
            }}
          >
            {surveyRoomList?.rooms?.map((room: any, index: number) => (
              <Box
                key={room.id}
                onClick={() => {
                  setSelectedRoomIndex(index);
                  setDirty(false);
                }}
                sx={{
                  padding: "6px 10px",
                  marginBottom: "6px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  border: "1px solid #1e8e6f",
                  background:
                    selectedRoomIndex === index ? "#b7d9c5" : "#ffffff",
                }}
              >
                <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                  {room.name.toUpperCase()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Grid>

      {/* RIGHT ITEMS */}
      <Grid item xs={8}>
        <Box
          sx={{
            border: "1px solid #d1cfcf",
            borderRadius: "6px",
            height: "70vh",
            overflow: "hidden",
            paddingBottom: "16px",
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              background: "#d7e3e2",
              padding: "6px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            Items
            <Button
              size="small"
              variant="contained"
              disabled={isInProgress}
              onClick={handleAddItem}
            >
              Add Item
            </Button>
          </Box>

          {/* ITEMS LIST */}
          <Box
            sx={{
              padding: "8px",
              height: "calc(100% - 32px)",
              overflowY: "auto",
            }}
          >
            {/* ADD NEW ITEM FIELD */}
            {showNewItemField && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  marginTop: "6px",
                  marginBottom:'6px',
                  justifyContent:'space-between'
                }}
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Enter item name"
                  value={newItem}
                  onChange={(e) => {
                    setNewItem(e.target.value);
                    setDirty(true);
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 30,
                      width:'100%'
                    },
                  }}
                />

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSaveNewItem}
                >
                  Save
                </Button>
              </Box>
            )}
            {defaultSurveyRoomItems?.items &&
            defaultSurveyRoomItems?.items?.length > 0 ? (
              defaultSurveyRoomItems?.items?.map((item: any) => (
                <Box
                  key={item.id}
                  sx={{
                    border: "1px solid #cfcfcf",
                    padding: "5px 8px",
                    borderRadius: "4px",
                    marginBottom: "6px",
                    background: "#f7f7f7",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {editItemId === item.id ? (
                    <>
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={(e) => {
                          setEditValue(e.target.value);
                          setDirty(true);
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            height: 28,
                          },
                        }}
                      />

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleSaveEdit}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: "13px" }}>
                        {item.name}
                      </Typography>

                      <IconButton
                        size="small"
                        disabled={isInProgress}
                        onClick={() => handleEdit(item)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              ))
            ) : (
              <Typography
                sx={{
                  textAlign: "center",
                  marginTop: "20px",
                  color: "#777",
                  fontSize: "13px",
                }}
              >
                No items available for this room.
              </Typography>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SurveyItemList;
